import React, { useId, useState } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useAttemptFeedback } from '../useAttemptFeedback';
import { useSaveFeedback } from '../useSaveFeedback';
import { FeedbackForm } from './FeedbackForm';

export interface FeedbackCardProps {
  attemptId: string;
  className?: string;
}

/**
 * The inline version, for the result and history pages. It shows the form until feedback
 * is left, then a short thank-you with an Edit button. It renders nothing while loading and
 * when the server refuses (an attempt that is not the viewer's, such as a teacher looking at
 * a candidate's result), so it can sit on shared pages without a guard of its own.
 */
export function FeedbackCard({ attemptId, className }: FeedbackCardProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const query = useAttemptFeedback(attemptId);
  const save = useSaveFeedback(attemptId);
  const [editing, setEditing] = useState(false);

  if (query.isLoading || query.isError) return null;
  const existing = query.data ?? null;

  return (
    <section aria-labelledby={headingId} className={cn('rounded-xl border border-border bg-card p-4', className)}>
      <h3 id={headingId} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {t('feedback.title')}
      </h3>

      {existing && !editing ? (
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Check className="h-4 w-4 text-success-600" aria-hidden="true" />
            {t('feedback.sent_title')}
          </p>
          <Button type="button" variant="outline" onClick={() => setEditing(true)}>
            {t('feedback.edit')}
          </Button>
        </div>
      ) : (
        <FeedbackForm
          initial={existing}
          pending={save.isPending}
          onLater={existing ? () => setEditing(false) : undefined}
          laterLabel={existing ? t('cancel') : undefined}
          onSubmit={async (input) => {
            try {
              await save.mutateAsync(input);
              setEditing(false);
            } catch {
              // Already reported by the hook; keep the form so the learner can try again.
            }
          }}
        />
      )}
    </section>
  );
}
